// src/offline/offlineDownload.ts

import api from "@/api/client";
import { db, type Address, type AddressState, type Customer, type NoteSynced } from "@/db";
import { TOTAL_PAGES, type Prefix } from "./offlinePrefixes";

export interface DownloadProgress {
  prefix: Prefix;
  currentPage: number;
  totalPages: number | null;
  addressesDownloaded: number;
}

export interface DownloadResult {
  ok: boolean;
  cancelled?: boolean;
  paused?: boolean;
  error?: string;
}

interface StoredProgress {
  lastPage: number;
  addressesDownloaded: number;
  nextCursor?: string | null;
}

export class DownloadController {
  cancelled = false;
  paused = false;
  cancel() {
    this.cancelled = true;
  }
  pause() {
    this.paused = true;
  }
}

// ------------------------------
// Helpers: network error detector
// ------------------------------
function isTransientNetworkError(err: unknown): boolean {
  const e = err as { message?: string; code?: string };
  const msg = String(e?.message || e);
  return (
    msg.includes("Network Error") ||
    msg.includes("Failed to fetch") ||
    msg.includes("Load failed") ||
    e?.code === "ECONNABORTED"
  );
}

function normalizeAddressForStorage(
  address: Omit<Address, "prefix">,
  prefix: Prefix
): Address {
  const toDisplay = address.toDisplay ?? address.to_display ?? "";
  return {
    ...address,
    prefix,
    toDisplay,
    to_display: address.to_display ?? toDisplay,
  };
}

// ------------------------------
// Helpers: Retry wrapper
// ------------------------------
async function safeRequest<T>(
  fn: () => Promise<T>,
  retryCount = 5
): Promise<T> {
  for (let i = 0; i < retryCount; i++) {
    try {
      return await fn();
    } catch (err) {
      if (!isTransientNetworkError(err)) throw err;
      await new Promise((res) => setTimeout(res, 500 * 2 ** i));
    }
  }
  throw new Error("Network unstable — try resume");
}

// ------------------------------
// Partial Progress Persistence
// ------------------------------
async function loadProgress(prefix: Prefix): Promise<StoredProgress | null> {
  const saved = await db.meta.get(`offlineDownloadProgress_${prefix}`);
  return (saved?.value ?? null) as StoredProgress | null;
}

async function saveProgress(
  prefix: Prefix,
  progress: StoredProgress
) {
  await db.meta.put({
    key: `offlineDownloadProgress_${prefix}`,
    value: progress,
  });
}

async function clearProgress(prefix: Prefix) {
  await db.meta.delete(`offlineDownloadProgress_${prefix}`);
}

// Exported for callers (e.g. cancel) that need to drop resume state.
export async function clearDownloadProgress(prefix: Prefix) {
  await clearProgress(prefix);
}

// ------------------------------
// MAIN DOWNLOAD FUNCTION
// ------------------------------
export async function downloadPrefix(
  prefix: Prefix,
  {
    onProgress,
    controller,
    pageSize = 5000,
  }: {
    onProgress?: (p: DownloadProgress) => void;
    controller?: DownloadController;
    pageSize?: number;
  }
): Promise<DownloadResult> {
  try {
    // -----------------------
    // Load previous progress
    // -----------------------
    const saved = await loadProgress(prefix);

    let pageCount = saved?.lastPage ?? 0;
    let totalDownloaded = saved?.addressesDownloaded ?? 0;

    const initialUrl = `/offline/bootstrap/?prefix=${prefix}&page_size=${pageSize}`;
    let nextUrl: string | null = saved?.nextCursor ?? initialUrl;

    // Initial “starting…” update
    onProgress?.({
      prefix,
      currentPage: pageCount,
      totalPages: TOTAL_PAGES[prefix] ?? null,
      addressesDownloaded: totalDownloaded,
    });

    // -------------------------------------
    // MAIN LOOP — supports resume + retries
    // -------------------------------------
    while (nextUrl && !controller?.cancelled) {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        return { ok: false, paused: true, error: "offline" };
      }
      if (controller?.paused) {
        return { ok: false, paused: true, error: "paused" };
      }
      pageCount++;

      const url = nextUrl as string;
      const res = await safeRequest(() => api.get(url));

      const {
        addresses = [],
        address_states = [],
        customers = [],
        notes = [],
        next,
      } = res.data;

      // Store in Dexie (chunking avoids background-write issues)
      await db.transaction(
        "rw",
        [
          db.addresses,
          db.addressStates,
          db.customers,
          db.notesSynced,
        ],
        async () => {
          if (addresses.length) {
            await db.addresses.bulkPut(
              addresses.map((a: Omit<Address, "prefix">) =>
                normalizeAddressForStorage(a, prefix)
              )
            );
          }

          if (address_states.length) {
            await db.addressStates.bulkPut(address_states.map((a: Omit<AddressState, "prefix">) => ({ ...a, prefix })));
          }

          if (customers.length) {
            await db.customers.bulkPut(customers.map((a: Omit<Customer, "prefix">) => ({ ...a, prefix })));
          }

          if (notes.length) {
            await db.notesSynced.bulkPut(notes.map((a: Omit<NoteSynced, "prefix">) => ({ ...a, prefix })));
          }
        }
      );

      totalDownloaded += addresses.length;

      // Save partial progress
      await saveProgress(prefix, {
        lastPage: pageCount,
        addressesDownloaded: totalDownloaded,
        nextCursor: next ?? null,
      });

      // Update UI
      onProgress?.({
        prefix,
        currentPage: pageCount,
        totalPages: TOTAL_PAGES[prefix] ?? null,
        addressesDownloaded: totalDownloaded,
      });

      nextUrl = next ?? null;
    }

    if (controller?.cancelled) {
      return { ok: false, cancelled: true };
    }

    // Finished successfully — clear resume state
    await clearProgress(prefix);

    return { ok: true };
  } catch (err: unknown) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}



// // src/offline/offlineDownload.ts
// import api from "@/api/client";
// import { db } from "../db";

// function isTransientNetworkError(err: any) {
//   const msg = String(err?.message || err);
//   return (
//     msg.includes("Network Error") ||
//     msg.includes("Failed to fetch") ||
//     msg.includes("Load failed") ||
//     err.code === "ECONNABORTED"
//   );
// }

// async function safeRequest<T>(fn: () => Promise<T>, retryCount = 5): Promise<T> {
//   for (let i = 0; i < retryCount; i++) {
//     try {
//       return await fn();
//     } catch (err) {
//       if (!isTransientNetworkError(err)) throw err;
//       await new Promise(r => setTimeout(r, 500 * 2 ** i)); // exponential backoff
//     }
//   }
//   throw new Error("Network unstable — try resume");
// }


// export interface DownloadProgress {
//   prefix: string;
//   currentPage: number;
//   totalPages: number | null;
//   addressesDownloaded: number;
// }

// export interface DownloadResult {
//   ok: boolean;
//   cancelled?: boolean;
//   error?: string;
// }

// export class DownloadController {
//   cancelled = false;
//   cancel() {
//     this.cancelled = true;
//   }
// }

// export async function downloadPrefix(
//   prefix: string,
//   {
//     onProgress,
//     controller,
//     pageSize = 5000,
//   }: {
//     onProgress?: (p: DownloadProgress) => void;
//     controller?: DownloadController;
//     pageSize?: number;
//   } = {}
// ): Promise<DownloadResult> {

//   let nextUrl = `/offline/bootstrap/?prefix=${prefix}&page_size=${pageSize}`;
//   let pageCount = 0;
//   let totalDownloaded = 0;

//   try {
//     while (nextUrl && !controller?.cancelled) {
//       pageCount++;

//       // Axios request through your client
//       const res = await safeRequest(() => api.get(nextUrl));

//       const {
//         addresses = [],
//         address_states = [],
//         customers = [],
//         notes = [],
//         next,
//       } = res.data;

//       // Write into Dexie
//       await db.transaction(
//         "rw",
//         [
//           db.addresses,
//           db.addressStates,
//           db.customers,
//           db.notesSynced,
//         ],
//         async () => {
//           if (addresses.length) {
//             const addrs = addresses.map((a: any) => ({
//               ...a,
//               prefix,
//             }));
//             await db.addresses.bulkPut(addrs);
//           }

//           if (address_states.length) {
//             const addrs_states = address_states.map((a: any) => ({
//               ...a,
//               prefix,
//             }));
//             await db.addressStates.bulkPut(addrs_states);
//           }

//           if (customers.length) {
//             const custs = customers.map((a: any) => ({
//               ...a,
//               prefix,
//             }));
//             await db.customers.bulkPut(custs);
//           }

//           if (notes.length) {
//             const nts = notes.map((a: any) => ({
//               ...a,
//               prefix,
//             }));
//             await db.notesSynced.bulkPut(nts);
//           }
//         }
//       );

//       totalDownloaded += addresses.length;

//       // Update progress UI
//       onProgress?.({
//         prefix,
//         currentPage: pageCount,
//         totalPages: null,
//         addressesDownloaded: totalDownloaded,
//       });

//       // Pagination continues
//       nextUrl = next;
//     }

//     if (controller?.cancelled) {
//       return { ok: false, cancelled: true };
//     }

//     return { ok: true };

//   } catch (err: any) {
//     return { ok: false, error: err.message };
//   }
// }
